// AlphaLab's header (design-revamp.md section 4): 48 px, the Quanty One logo with a smaller AlphaLab
// logo ("by AlphaLab") on the left; Scheduled, Open AlphaLab and the person's menu (with the theme
// setting, design-revamp.md section 4a) on the right. What
// fits grows with the width: the Quanty One mark alone under 640 px, the wordmark from there, the
// controls' labels from 768 px and "by AlphaLab" from 1024 px, so the two sides never meet.
import * as Popover from "@radix-ui/react-popover";
import { CalendarClock, ExternalLink, ListChecks, Monitor, Moon, RotateCcw, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { currentTheme, darkNow, onThemeChange, setTheme, type ThemeSetting } from "../theme.js";
import { Button } from "./ui/button.js";

export function useDark(): boolean {
  const [dark, setDark] = useState(darkNow());
  useEffect(() => onThemeChange(() => setDark(darkNow())), []);
  return dark;
}

function useThemeSetting(): ThemeSetting {
  const [setting, setSetting] = useState(currentTheme());
  useEffect(() => onThemeChange(() => setSetting(currentTheme())), []);
  return setting;
}

const THEME_CHOICES: readonly { value: ThemeSetting; label: string; Icon: typeof Sun }[] = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

/** The theme setting: three buttons, the chosen one filled. */
export function ThemeChoice() {
  const setting = useThemeSetting();
  return (
    <div role="radiogroup" aria-label="Theme" className="flex gap-1">
      {THEME_CHOICES.map(({ value, label, Icon }) => (
        <Button
          key={value}
          role="radio"
          aria-checked={setting === value}
          variant={setting === value ? "secondary" : "ghost"}
          size="sm"
          className="flex-1"
          onClick={() => setTheme(value)}
        >
          <Icon />
          {label}
        </Button>
      ))}
    </div>
  );
}

export function Logo({ className = "h-5" }: { className?: string }) {
  const dark = useDark();
  return <img src={dark ? "/AlphaLab-logo-white.svg" : "/AlphaLab-logo-black.svg"} alt="AlphaLab" className={className} />;
}

/** The Quanty One wordmark (public/quanty-logo-*.png), in the theme's colour. */
export function QuantyLogo({ className = "h-7" }: { className?: string }) {
  const dark = useDark();
  return <img src={dark ? "/quanty-logo-white.png" : "/quanty-logo-black.png"} alt="Quanty One" className={className} />;
}

/** The Quanty One mark alone (public/quanty-mark-*.png), for narrow screens. */
export function QuantyMark({ className = "size-7" }: { className?: string }) {
  const dark = useDark();
  return <img src={dark ? "/quanty-mark-white.png" : "/quanty-mark-black.png"} alt="Quanty One" className={`object-contain ${className}`} />;
}

/** "by AlphaLab": the AlphaLab logo, smaller than the Quanty One one it follows. */
export function ByAlphaLab({ className = "" }: { className?: string }) {
  return (
    <span className={`flex items-center gap-1.5 text-xs text-muted-foreground ${className}`}>
      <span>by</span>
      <Logo className="h-3.5" />
    </span>
  );
}

function initials(email: string | undefined): string {
  const name = (email ?? "?").split("@")[0];
  const parts = name.split(/[._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

export interface HeaderProps {
  email?: string;
  alphalabUrl?: string;
  scheduledOpen: boolean;
  onScheduled(): void;
  tasksOpen: boolean;
  onTasks(): void;
  onOpenAlphaLab(url: string): void;
  onLogOut(): void;
  onSignOutEverywhere(): void;
  /** Asks to reset Quanty One's conversation (App confirms first). */
  onResetConversation(): void;
}

export function Header({ email, alphalabUrl, scheduledOpen, onScheduled, tasksOpen, onTasks, onOpenAlphaLab, onLogOut, onSignOutEverywhere, onResetConversation }: HeaderProps) {
  return (
    <header className="relative z-50 flex h-12 shrink-0 items-center justify-between border-b border-border bg-background px-4">
      <div className="flex min-w-0 items-center gap-2.5">
        <a href="/" aria-label="Quanty One home" className="flex h-11 items-center">
          <span className="hidden sm:inline">
            <QuantyLogo className="h-6" />
          </span>
          <QuantyMark className="size-7 sm:hidden" />
        </a>
        <ByAlphaLab className="hidden pt-0.5 lg:flex" />
      </div>
      <div className="flex items-center gap-2 md:gap-4">
        <Button variant="ghost" size="sm" onClick={onResetConversation} aria-label="Reset conversation">
          <RotateCcw />
          <span className="hidden md:inline">Reset</span>
        </Button>
        <Button variant={tasksOpen ? "secondary" : "ghost"} size="sm" onClick={onTasks} aria-pressed={tasksOpen}>
          <ListChecks />
          <span className="hidden md:inline">Tasks</span>
        </Button>
        <Button variant={scheduledOpen ? "secondary" : "ghost"} size="sm" onClick={onScheduled} aria-pressed={scheduledOpen}>
          <CalendarClock />
          <span className="hidden md:inline">Scheduled</span>
        </Button>
        {alphalabUrl && (
          <Button asChild variant="ghost" size="sm">
            <a
              href={alphalabUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                onOpenAlphaLab(alphalabUrl);
              }}
            >
              <ExternalLink />
              <span className="hidden md:inline">Open AlphaLab</span>
            </a>
          </Button>
        )}
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              type="button"
              aria-label="Account"
              className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
            >
              {initials(email)}
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content align="end" sideOffset={8} className="z-50 w-64 rounded-lg border border-border bg-popover p-2 text-popover-foreground shadow-md">
              <p className="truncate px-2 py-1.5 text-sm text-muted-foreground">{email}</p>
              <div className="my-1 h-px bg-border" />
              <p className="px-2 pt-1 pb-1.5 text-xs text-muted-foreground">Theme</p>
              <ThemeChoice />
              <div className="my-1 h-px bg-border" />
              <Button variant="ghost" size="sm" className="w-full justify-start" onClick={onLogOut}>
                Log out
              </Button>
              <Button variant="ghost" size="sm" className="w-full justify-start" onClick={onSignOutEverywhere}>
                Sign out everywhere
              </Button>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    </header>
  );
}
